import SearchHeader from "@components/common/SearchHeader";
import NavigationBar from "@components/NotificationBar";
import { getColorStyle } from "@common/color";
import { convertCurrentDateWithTime } from "@common/date-helpers";
import { QueryRequestHelper } from "@common/query-request-helper";
import DashboardLayout from "@layouts/dashboard/dashboard-layout";
import { getValue } from "@utils/lodash";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PaginationPage from "@components/pagination/pagination";
import { getAllGL, getGLImportTemplate, updateGLBulk } from "@services/gl.service";
import { toast } from "react-toastify";

function AdminModuleGL(props: any) {
  const navigate = useNavigate();
  const location = useLocation();
  const [search_text, setSearchInput] = useState<any>("");
  const [showSearchTextbox, setShowSearchTextbox] = useState(false);
  const [glList, setGlList] = useState<any[]>([]);
  const [selectedIDs, setSelectedIDs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [limit, setLimit] = useState(10);
  const [page_no, setPage_no] = useState(1);
  const [templateURL, setTemplateURL] = useState<Blob | null>(null);

  const isAllSelected =
    getValue(glList, `length`, 0) > 0 &&
    glList.every((item: any) =>
      selectedIDs.includes(getValue(item, `id`, ""))
    );

  const getData = async () => {
    const queryParams = new URLSearchParams(location.search);
    const ids = [
      ...queryParams.getAll("ids"),
      ...queryParams.getAll("ids[]"),
    ]
      .map(Number)
      .filter((id) => Number.isFinite(id));
    const payload = {
      status: queryParams.get("status") || undefined,
      ids: ids.length > 0 ? ids : undefined,
      id: queryParams.get("id")
        ? Number(queryParams.get("id"))
        : undefined,
      search_text: queryParams.get("search_text") || undefined,
      page_no: Number(queryParams.get("page_no")) || 1,
      page_size: Number(queryParams.get("page_size")) || 10,
      sort_by: queryParams.get("sort_by") || undefined,
    };

    try {
      setIsLoading(true);
      const resp = await getAllGL(QueryRequestHelper(payload));
      const responseData = getValue(resp, `data`, resp);
      const records = Array.isArray(responseData)
        ? responseData
        : getValue(
            responseData,
            `data`,
            getValue(
              responseData,
              `items`,
              getValue(responseData, `results`, [])
            )
          );
      const result = Array.isArray(records) ? records : [];
      setGlList(result);
      setTotalCount(
        getValue(
          responseData,
          `pagination.total`,
          getValue(responseData, `total`, result.length)
        )
      );
    } catch (error) {
      setGlList([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };



  const handleChangeActiveStatus = async (value: string) => {
    try {
      const resp = await updateGLBulk({
        ids: selectedIDs,
        is_active: value === "active" ? true : false,
      });
      if (resp) {
        toast.success(getValue(resp, `message`, ""));
        setSelectedIDs([]);
        getData();
      } else {
        getData();
      }
    } catch (error) {}
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const currentSearch = queryParams.get("search_text") || "";
    setSearchInput(currentSearch);
    setShowSearchTextbox(Boolean(currentSearch));
    setPage_no(Number(queryParams.get("page_no")) || 1);
    setLimit(Number(queryParams.get("page_size")) || 10);
    if (props.permissionAPITriggered) getData();
  }, [location.search, props.permissionAPITriggered]);

  useEffect(() => {
    const fetchTemplateURL = async () => {
      try {
        const resp = await getGLImportTemplate();
        setTemplateURL(resp instanceof Blob ? resp : null);
      } catch (error) {
        setTemplateURL(null);
      }
    };

    fetchTemplateURL();
  }, []);

  const handleChangePagination = (page: string) => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.set("page_no", page);
    navigate(`${location.pathname}?${queryParams.toString()}`);
  };

  const handleChangeLimit = (pageSize: string) => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.set("page_size", pageSize);
    queryParams.set("page_no", "1");
    navigate(`${location.pathname}?${queryParams.toString()}`);
  };

  const handleChangeSearch = (event: any) => {
    const value = event.target.value;
    setSearchInput(value);
    const queryParams = new URLSearchParams(location.search);
    if (value) queryParams.set("search_text", value);
    else queryParams.delete("search_text");
    queryParams.set("page_no", "1");
    navigate(`${location.pathname}?${queryParams.toString()}`);
    if (!value) getData();
  };

  const toggleSearch = () => setShowSearchTextbox((visible) => !visible);

  const toggleSearchClose = () => {
    setSearchInput("");
    toggleSearch();
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.has("search_text")) {
      queryParams.delete("search_text");
      navigate(`${location.pathname}?${queryParams.toString()}`);
      getData();
    }
  };

  const handleCheckUsers = (id: string) => {
    setSelectedIDs((prevSelected: any[]) => {
      if (prevSelected.includes(id)) {
        return prevSelected.filter((item: string) => item !== id);
      }
      return [...prevSelected, id];
    });
  };

  const handleCheckAll = (status: string) => {
    if (status === "All") {
      setSelectedIDs([]);
    } else {
      const ids = glList.map((item: any) => getValue(item, `id`, ""));
      setSelectedIDs(ids);
    }
  };

  const handleRemoveNavItems = () => {
    setSelectedIDs([]);
  };

  const handleNavigate = (id: string) => {
    navigate(`/admin/modules/GL/detail/${id}`);
  };

  return (
    <DashboardLayout
      permissions={getValue(props, `tabPermissionList`, [])}
      subTabPermissionList={getValue(props, `subTabPermissionList`, [])}
    >
      <div className="dashboard-wrapper__header">
        <h4 className="dashbaord-wrapper__header-title">GL</h4>
      </div>

      {getValue(selectedIDs, `length`, 0) > 0 && (
        <NavigationBar
          selectedIDs={selectedIDs}
          handleChangeActiveStatus={handleChangeActiveStatus}
          multi={true}
          permissions={getValue(props, `permissions`, [])}
          handleRemoveNavItems={handleRemoveNavItems}
        />
      )}

      <div className="admin-wrapper position-relative">
        <SearchHeader
          searchInput={search_text}
          showSearchTextbox={showSearchTextbox}
          toggleSearch={toggleSearch}
          toggleSearchClose={toggleSearchClose}
          handleChangeSearch={handleChangeSearch}
          route={"/admin/modules/GL/add"}
          commonUpload={true}
          download={true}
          download_type={"GL"}
          dropdownOptions={[{ label: "GL", value: "GL" }]}
          downloadURL={templateURL}
        />
      </div>

      <div
        className={
          getValue(selectedIDs, `length`, 0) > 0
            ? "datatable_main_container_notifiction"
            : "datatable_main_container"
        }
        style={{ overflowY: "auto" }}
      >
        <table className="datatable_table" style={{ width: "100%" }}>
          <thead className="datatable_table_thead">
            <tr>
              <th className="datatable_table_th" style={{ width: "70px" }}>
                <label className="m-0 position-relative" htmlFor="header-checkbox">
                  <input
                    type="checkbox"
                    className="default-checkbox"
                    id="header-checkbox"
                    onChange={() =>
                      handleCheckAll(isAllSelected ? "All" : "None")
                    }
                    checked={isAllSelected}
                  />
                  <div className="custom-checkbox">
                    <img
                      src="/images/uploaded-details/tick.svg"
                      className="img-fluid uploaded-tick"
                      alt="selected"
                    />
                  </div>
                </label>
              </th>
              <th className="datatable_table_th">
                <h6>GL Name</h6>
              </th>
              <th className="datatable_table_th">
                <h6>GL Code</h6>
              </th>
              <th className="datatable_table_th">
                <h6>Last updated date</h6>
              </th>
              <th className="datatable_table_th">
                <h6>Status</h6>
              </th>
            </tr>
          </thead>
          <tbody className="datatable_table_tbody">
            {isLoading ? (
              <tr className="datatable_table_tr">
                <td colSpan={5} className="datatable_table_td text-center">
                  <h6 className="mb-0">Loading GL records...</h6>
                </td>
              </tr>
            ) : glList.length > 0 ? (
              glList.map((item: any, index: number) => (
                <tr key={item.id || index} className="datatable_table_tr">
                  <td className="datatable_table_td" style={{ width: "70px" }}>
                    <label className="m-0 position-relative" htmlFor={`gl-row-${item.id || index}`}>
                      <input
                        type="checkbox"
                        className="default-checkbox"
                        id={`gl-row-${item.id || index}`}
                        onChange={() => handleCheckUsers(getValue(item, `id`, ""))}
                        checked={selectedIDs.includes(getValue(item, `id`, ""))}
                      />
                      <div className="custom-checkbox">
                        <img
                          src="/images/uploaded-details/tick.svg"
                          className="img-fluid uploaded-tick"
                          alt="selected"
                        />
                      </div>
                    </label>
                  </td>
                  <td
                    className="datatable_table_td"
                    onClick={() => handleNavigate(getValue(item, `id`, ""))}
                  >
                    <h6 className="color_primary">{getValue(item, `gl_name`, "")}</h6>
                  </td>
                  <td
                    className="datatable_table_td"
                    onClick={() => handleNavigate(getValue(item, `id`, ""))}
                  >
                    <h6>{getValue(item, `gl_code`, "")}</h6>
                  </td>
                  <td
                    className="datatable_table_td"
                    onClick={() => handleNavigate(getValue(item, `id`, ""))}
                  >
                    <h6>
                      {getValue(item, `updated_at`, null)
                        ? convertCurrentDateWithTime(
                            getValue(item, `updated_at`, null)
                          )
                        : null}
                    </h6>
                  </td>
                  <td
                    className="datatable_table_td"
                    onClick={() => handleNavigate(getValue(item, `id`, ""))}
                  >
                    <h6
                      style={getColorStyle(
                        getValue(item, `is_active`, false)
                          ? "Active"
                          : "Inactive"
                      )}
                    >
                      {getValue(item, `is_active`, false)
                        ? "Active"
                        : "Inactive"}
                    </h6>
                  </td>
                </tr>
              ))
            ) : (
              <tr className="datatable_table_tr">
                <td colSpan={5} className="datatable_table_td text-center">
                  <h6 className="mb-0">No GL records found.</h6>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="fixed_footer_wrapper d-flex justify-content-between">
        {totalCount > limit ? (
          <PaginationPage
            totalCount={totalCount}
            limit={limit}
            pageNumber={page_no}
            handleChangePagination={handleChangePagination}
            handleChangeLimit={handleChangeLimit}
          />
        ) : (
          <p className="show-rows-count mt-3 d-flex">
            Total Count:&nbsp;&nbsp;
            <span style={{ color: "rgb(13, 110, 253)" }}>{totalCount}</span>
          </p>
        )}
      </div>
    </DashboardLayout>
  );
}

export default AdminModuleGL;
